import http from 'hub-http/clients/apiClient';
import { dataFetchingClient } from '../../utils/DataFetchingClient';
import { registerMutation, registerQuery, unwrapHubHttpError, useMutation, useQuery } from 'academy-common-lib/utils/DataFetchingClient';
import { BASE_URI_PROGRESS } from '../../constants/ApiConstants';
import getLang from 'LearningCenterUI/utils/getLang';
import { useCallback, useMemo } from 'react';
import { updateCache } from '../../utils/DataFetchingClientCache';
import maxBy from 'hs-lodash/maxBy';
import findIndex from 'hs-lodash/findIndex';
import orderBy from 'hs-lodash/orderBy';
import { useFullTrack } from '../tracks/tracksQueries';
import { getPlaylistIdFromUrl } from '../../utils/UrlParamUtils';
import remove from 'hs-lodash/remove';
import { useGetUserProgressQuery } from '../../../__generated__/chirp/com/hubspot/userprogress/bff/rpc/UserProgressBFFChirpService.hooks';
import { MY_LEARNING_QUERY_FIELD_NAME } from '../myLearning/myLearningQueries';

/** Registered queries */

export const GET_ALL_PROGRESS = registerQuery({
  fieldName: 'allProgress',
  args: ['language'],
  fetcher({
    language = getLang()
  }) {
    return http.get(`${BASE_URI_PROGRESS}/user-progress/user/${language}`, {
      timeout: 25000
    }).then(response => response);
  }
});
const GET_TRACK_PROGRESS = registerQuery({
  fieldName: 'trackProgress',
  args: ['trackId'],
  fetcher({
    trackId
  }) {
    return http.get(`${BASE_URI_PROGRESS}/user-progress/track/${trackId}`).then(response => response);
  }
});
const GET_LESSON_PROGRESS = registerQuery({
  fieldName: 'lessonProgress',
  args: ['lessonId'],
  fetcher({
    lessonId
  }) {
    return http.get(`${BASE_URI_PROGRESS}/user-progress/lesson/${lessonId}`).then(response => response);
  }
});
export const GET_PLAYLIST_PROGRESS = registerQuery({
  fieldName: 'playlistProgress',
  args: ['playlistId'],
  fetcher({
    playlistId
  }) {
    return http.get(`${BASE_URI_PROGRESS}/user-progress/playlist/${playlistId}`).then(response => {
      const progress = response;
      return Object.assign({}, progress, {
        itemsProgress: orderBy(progress.itemsProgress, ['updatedAt'], ['desc'])
      });
    });
  }
});
export const usePlaylistProgress = (playlistId, skip) => {
  const {
    data: playlistProgress,
    loading: playlistProgressLoading,
    error: playlistProgressError
  } = useQuery(GET_PLAYLIST_PROGRESS, {
    variables: {
      playlistId
    },
    skip
  });
  return {
    playlistProgress,
    playlistProgressLoading,
    playlistProgressError
  };
};
export const UPDATE_CONTENT_PROGRESS = registerMutation({
  fieldName: 'updateContentProgress',
  args: ['contentId', 'trackId', 'lessonId', 'playlistId', 'language'],
  fetcher({
    contentId,
    trackId,
    lessonId,
    playlistId,
    language
  }) {
    return http.post(`${BASE_URI_PROGRESS}/user-progress/content/${contentId}`, {
      data: {
        trackId,
        lessonId,
        playlistId,
        language
      }
    });
  }
});

/** Cache updator functions */

export const deleteContentProgressFromCache = ({
  trackId,
  lessonId,
  contentId
}) => {
  updateCache(GET_LESSON_PROGRESS, lessonProgress => {
    remove(lessonProgress.contentProgress, c => c.contentId === contentId);
  }, {
    lessonId
  });
  if (trackId) updateCache(GET_TRACK_PROGRESS, trackProgress => {
    const lessonProgress = trackProgress.progress.find(p => p.lessonId === lessonId);
    if (!lessonProgress) return;
    remove(lessonProgress.contentProgress, c => c.contentId === contentId);
  }, {
    trackId
  });
};
const updateLessonProgressWithContentProgress = (lessonProgress, contentId, status) => {
  const content = lessonProgress.contentProgress.find(c => c.contentId === contentId);
  if (content) {
    content.progressType = status;
  } else {
    lessonProgress.contentProgress.push({
      contentId,
      progressType: status,
      updatedAt: new Date().getTime()
    });
  }
};
export const setContentProgressStatusCache = ({
  trackId,
  lessonId,
  contentId,
  status
}) => {
  updateCache(GET_LESSON_PROGRESS, lessonProgress => {
    updateLessonProgressWithContentProgress(lessonProgress, contentId, status);
  }, {
    lessonId
  });
  if (trackId) updateCache(GET_TRACK_PROGRESS, trackProgress => {
    const lessonProgress = trackProgress.progress.find(p => p.lessonId === lessonId);
    if (!lessonProgress) return;
    updateLessonProgressWithContentProgress(lessonProgress, contentId, status);
  }, {
    trackId
  });
};
export const setMostRecentExamAttemptCache = (trackId, attemptData) => {
  updateCache(GET_TRACK_PROGRESS, trackProgress => {
    trackProgress.examAttempts[attemptData.id] = attemptData;
    trackProgress.examIdToRecentAttemptId[attemptData.examId] = attemptData.id;
  }, {
    trackId
  });
};
export const addQuestionSubmissionCache = (trackId, attemptId, questionId, answerIds) => {
  updateCache(GET_TRACK_PROGRESS, trackProgress => {
    trackProgress.examAttempts[attemptId].questionSubmissions = [...trackProgress.examAttempts[attemptId].questionSubmissions, {
      questionId,
      answerIds
    }];
  }, {
    trackId
  });
};
export const setLessonQuizAttemptCache = (lessonId, quizId, attemptData, trackId) => {
  updateCache(GET_LESSON_PROGRESS, lessonProgress => {
    lessonProgress.quizIdToRecentAttemptId[quizId] = attemptData.id;
    lessonProgress.quizAttempts[attemptData.id] = attemptData;
  }, {
    lessonId
  });
  if (trackId) updateCache(GET_TRACK_PROGRESS, trackProgress => {
    const lessonProgress = trackProgress.progress.find(p => p.lessonId === lessonId);
    if (!lessonProgress) return;
    lessonProgress.quizIdToRecentAttemptId[quizId] = attemptData.id;
    lessonProgress.quizAttempts[attemptData.id] = attemptData;
  }, {
    trackId
  });
};
export const clearLessonQuizAttemptCache = ({
  trackId,
  lessonId,
  quizId,
  attemptId
}) => {
  updateCache(GET_LESSON_PROGRESS, lessonProgress => {
    delete lessonProgress.quizIdToRecentAttemptId[quizId];
    delete lessonProgress.quizAttempts[attemptId];
  }, {
    lessonId
  });
  if (trackId) updateCache(GET_TRACK_PROGRESS, trackProgress => {
    const lessonProgress = trackProgress.progress.find(p => p.lessonId === lessonId);
    if (!lessonProgress) return;
    delete lessonProgress.quizIdToRecentAttemptId[quizId];
    delete lessonProgress.quizAttempts[attemptId];
  }, {
    trackId
  });
};
const updateLessonProgressWithQuizAttemptAnswer = (lessonProgress, attemptId, questionId, answerIds, correctAnswer) => {
  lessonProgress.quizAttempts[attemptId].answerSubmissions.push({
    quizAttemptId: attemptId,
    id: 0,
    questionId,
    answerIds,
    correctAnswer,
    submissionTime: new Date().getTime()
  });
};
export const addLessonQuizAttemptAnswerCache = (lessonId, attemptId, questionId, answerIds, correctAnswer, trackId) => {
  updateCache(GET_LESSON_PROGRESS, lessonProgress => {
    updateLessonProgressWithQuizAttemptAnswer(lessonProgress, attemptId, questionId, answerIds, correctAnswer);
  }, {
    lessonId
  });
  if (trackId) updateCache(GET_TRACK_PROGRESS, trackProgress => {
    const lessonProgress = trackProgress.progress.find(p => p.lessonId === lessonId);
    if (!lessonProgress) return;
    updateLessonProgressWithQuizAttemptAnswer(lessonProgress, attemptId, questionId, answerIds, correctAnswer);
  }, {
    trackId
  });
};
const updateLessonProgressWithStartQuizAttempt = (lessonProgress, attemptId, questionId, answerIds, correctAnswer, startQuizInfo) => {
  lessonProgress.quizIdToRecentAttemptId[startQuizInfo.quizId] = attemptId;
  lessonProgress.quizAttempts[attemptId] = Object.assign({}, startQuizInfo.firstAttemptData, {
    answerSubmissions: [...startQuizInfo.firstAttemptData.answerSubmissions, {
      quizAttemptId: attemptId,
      id: 0,
      questionId,
      answerIds,
      correctAnswer,
      submissionTime: new Date().getTime()
    }]
  });
};
export const addLessonStartQuizAttemptAnswerCache = (lessonId, attemptId, questionId, answerIds, correctAnswer, startQuizInfo, trackId) => {
  updateCache(GET_LESSON_PROGRESS, lessonProgress => {
    updateLessonProgressWithStartQuizAttempt(lessonProgress, attemptId, questionId, answerIds, correctAnswer, startQuizInfo);
  }, {
    lessonId
  });
  if (trackId) updateCache(GET_TRACK_PROGRESS, trackProgress => {
    const lessonProgress = trackProgress.progress.find(p => p.lessonId === lessonId);
    if (!lessonProgress) return;
    updateLessonProgressWithStartQuizAttempt(lessonProgress, attemptId, questionId, answerIds, correctAnswer, startQuizInfo);
  }, {
    trackId
  });
};
const updateLessonProgressWithQuizAttemptAnswerCache = (lessonProgress, attemptId, questionId) => {
  lessonProgress.quizAttempts[attemptId].answerSubmissions = lessonProgress.quizAttempts[attemptId].answerSubmissions.filter(answer => answer.questionId !== questionId);
};
export const removeLessonQuizAttemptAnswerCache = ({
  trackId,
  lessonId,
  attemptId,
  questionId
}) => {
  updateCache(GET_LESSON_PROGRESS, lessonProgress => {
    updateLessonProgressWithQuizAttemptAnswerCache(lessonProgress, attemptId, questionId);
  }, {
    lessonId
  });
  if (trackId) updateCache(GET_TRACK_PROGRESS, trackProgress => {
    const lessonProgress = trackProgress.progress.find(p => p.lessonId === lessonId);
    if (!lessonProgress) return;
    updateLessonProgressWithQuizAttemptAnswerCache(lessonProgress, attemptId, questionId);
  }, {
    trackId
  });
};

/** Core query hooks */

export const useAllProgress = (variables, options = {}) => {
  const {
    language
  } = variables || {};
  const {
    data: allProgress,
    loading: allProgressLoading,
    error: allProgressError
  } = useQuery(GET_ALL_PROGRESS, {
    variables: {
      language: language || getLang()
    },
    fetchPolicy: options.fetchPolicy,
    pollInterval: options.polling ? 1000 : 0,
    skip: options.skip,
    context: {
      timeout: options.timeout || 25000
    }
  });
  return {
    allProgress: allProgress || {
      assetProgress: [],
      contentProgress: {}
    },
    allProgressLoading,
    allProgressError
  };
};
export const useTrackProgressById = trackId => {
  const {
    data: trackProgressById,
    loading: trackProgressByIdLoading,
    error: trackProgressByIdError,
    refetch: trackProgressByIdRefetch
  } = useQuery(GET_TRACK_PROGRESS, {
    variables: {
      trackId: trackId
    },
    skip: !trackId || isNaN(Number(trackId)),
    onCompleted(data) {
      data.trackProgress.progress.forEach(lessonProgress => {
        updateCache(GET_LESSON_PROGRESS, () => lessonProgress, {
          lessonId: lessonProgress.lessonId
        });
      });
    }
  });
  return {
    trackProgressById,
    trackProgressByIdLoading,
    trackProgressByIdError,
    trackProgressByIdRefetch
  };
};
export const useLessonProgressById = lessonId => {
  const {
    data: lessonProgressById,
    loading: lessonProgressByIdLoading,
    error: lessonProgressByIdError,
    refetch: lessonProgressByIdRefetch
  } = useQuery(GET_LESSON_PROGRESS, {
    variables: {
      lessonId: Number(lessonId)
    },
    skip: !lessonId
  });
  return {
    lessonProgressById,
    lessonProgressByIdLoading,
    lessonProgressByIdError,
    lessonProgressByIdRefetch
  };
};
export const START_PLAYLIST_PROGRESS = registerMutation({
  fieldName: 'startPlaylistProgress',
  args: ['playlistId'],
  fetcher({
    playlistId
  }) {
    return http.post(`${BASE_URI_PROGRESS}/asset-progress/start/playlist/${playlistId}`);
  }
});
export const useStartPlaylistProgress = ({
  playlistId,
  language
}) => {
  const playlistIdToUse = playlistId || getPlaylistIdFromUrl();
  const [startPlaylistProgress, {
    loading: startPlaylistProgressLoading,
    called: startPlaylistProgressCalled,
    error: startPlaylistProgressCalledError
  }] = useMutation(START_PLAYLIST_PROGRESS, {
    variables: {
      playlistId
    },
    refetchQueries: [{
      query: GET_ALL_PROGRESS,
      variables: {
        language
      },
      context: {
        timeout: 25000
      }
    }, ...(playlistIdToUse ? [{
      query: GET_PLAYLIST_PROGRESS,
      variables: {
        playlistId: playlistIdToUse
      }
    }] : [])],
    update: (cache, result) => {
      const {
        data,
        errors
      } = result;
      if (!data || errors && errors.length) return;
      cache.evict({
        fieldName: MY_LEARNING_QUERY_FIELD_NAME
      });
    }
  });
  return [startPlaylistProgress, {
    startPlaylistProgressLoading,
    startPlaylistProgressCalled,
    startPlaylistProgressCalledError
  }];
};
export const START_TRACK_PROGRESS = registerMutation({
  fieldName: 'startTrackProgress',
  args: ['language', 'trackId', 'playlistId'],
  fetcher({
    language,
    trackId,
    playlistId
  }) {
    return http.post(`${BASE_URI_PROGRESS}/asset-progress/start/track/${trackId}`, {
      data: {
        playlistId,
        language
      }
    });
  }
});
export const useStartTrackProgress = ({
  language,
  trackId,
  playlistId
}) => {
  const playlistIdToUse = playlistId || getPlaylistIdFromUrl();
  const [startTrackProgress, {
    loading: startTrackProgressLoading,
    called: startTrackProgressCalled,
    error: startTrackProgressError
  }] = useMutation(START_TRACK_PROGRESS, {
    variables: {
      language,
      trackId: trackId,
      playlistId
    },
    refetchQueries: [{
      query: GET_ALL_PROGRESS,
      variables: {
        language
      },
      context: {
        timeout: 25000
      }
    }, {
      query: GET_TRACK_PROGRESS,
      variables: {
        trackId
      }
    }, ...(playlistIdToUse ? [{
      query: GET_PLAYLIST_PROGRESS,
      variables: {
        playlistId: playlistIdToUse
      }
    }] : [])],
    update: (cache, result) => {
      const {
        data,
        errors
      } = result;
      if (!data || errors && errors.length) return;
      cache.evict({
        fieldName: MY_LEARNING_QUERY_FIELD_NAME
      });
    }
  });
  return [startTrackProgress, {
    startTrackProgressLoading,
    startTrackProgressCalled,
    startTrackProgressError
  }];
};
export const START_LESSON_PROGRESS = registerMutation({
  fieldName: 'startLessonProgress',
  args: ['language', 'lessonId', 'playlistId'],
  fetcher({
    language,
    lessonId,
    playlistId
  }) {
    return http.post(`${BASE_URI_PROGRESS}/asset-progress/start/lesson/${lessonId}`, {
      data: {
        playlistId,
        language
      }
    });
  }
});
export const useStartLessonProgress = ({
  language,
  lessonId,
  playlistId
}) => {
  const playlistIdToUse = playlistId || getPlaylistIdFromUrl();
  const [startLessonProgress, {
    loading: startLessonProgressLoading,
    called: startLessonProgressCalled,
    error: startLessonProgressError
  }] = useMutation(START_LESSON_PROGRESS, {
    variables: {
      language,
      lessonId: lessonId,
      playlistId
    },
    refetchQueries: [{
      query: GET_ALL_PROGRESS,
      variables: {
        language
      },
      context: {
        timeout: 25000
      }
    }, {
      query: GET_LESSON_PROGRESS,
      variables: {
        lessonId
      }
    }, ...(playlistIdToUse ? [{
      query: GET_PLAYLIST_PROGRESS,
      variables: {
        playlistId: playlistIdToUse
      }
    }] : [])],
    update: (cache, result) => {
      const {
        data,
        errors
      } = result;
      if (!data || errors && errors.length) return;
      cache.evict({
        fieldName: MY_LEARNING_QUERY_FIELD_NAME
      });
    }
  });
  return [startLessonProgress, {
    startLessonProgressLoading,
    startLessonProgressCalled,
    startLessonProgressError
  }];
};
export const useUpdateContentProgress = () => {
  const [mutate, {
    loading: updateContentProgressLoading,
    called: updateContentProgressCalled,
    error: updateContentProgressError,
    data: updateContentProgressData
  }] = useMutation(UPDATE_CONTENT_PROGRESS, {
    update: (cache, result) => {
      const {
        data,
        errors
      } = result;
      if (!data || errors && errors.length) return;
      cache.evict({
        fieldName: MY_LEARNING_QUERY_FIELD_NAME
      });
    }
  });
  const updateContentProgress = useCallback(({
    contentId,
    lessonId,
    trackId,
    language
  }) => {
    const playlistId = getPlaylistIdFromUrl();
    return mutate({
      awaitRefetchQueries: true,
      variables: {
        contentId,
        lessonId,
        trackId,
        playlistId,
        language
      },
      refetchQueries: [{
        query: GET_ALL_PROGRESS,
        variables: {
          language
        },
        context: {
          timeout: 25000
        }
      }, ...(lessonId ? [{
        query: GET_LESSON_PROGRESS,
        variables: {
          lessonId
        }
      }] : []), ...(trackId ? [{
        query: GET_TRACK_PROGRESS,
        variables: {
          trackId
        }
      }] : []), ...(playlistId ? [{
        query: GET_PLAYLIST_PROGRESS,
        variables: {
          playlistId
        }
      }] : [])]
    });
  }, [mutate]);
  return [updateContentProgress, {
    updateContentProgressLoading,
    updateContentProgressCalled,
    updateContentProgressError,
    updateContentProgressData
  }];
};
export const updateContentProgress = ({
  contentId,
  lessonId,
  trackId,
  language
}) => {
  const playlistId = getPlaylistIdFromUrl();
  return dataFetchingClient.mutate({
    mutation: UPDATE_CONTENT_PROGRESS,
    variables: {
      contentId,
      lessonId,
      trackId,
      language,
      playlistId
    },
    refetchQueries: [{
      query: GET_ALL_PROGRESS,
      variables: {
        language
      },
      context: {
        timeout: 25000
      }
    }, ...(lessonId ? [{
      query: GET_LESSON_PROGRESS,
      variables: {
        lessonId
      }
    }] : []), ...(trackId ? [{
      query: GET_TRACK_PROGRESS,
      variables: {
        trackId
      }
    }] : []), ...(playlistId ? [{
      query: GET_PLAYLIST_PROGRESS,
      variables: {
        playlistId
      }
    }] : [])],
    update: (cache, result) => {
      const {
        data,
        errors
      } = result;
      if (!data || errors && errors.length) return;
      cache.evict({
        fieldName: MY_LEARNING_QUERY_FIELD_NAME
      });
    }
  }).then(result => result.data);
};
export const useAssetStatus = ({
  id,
  type,
  language,
  skip
}) => {
  let status = 'NOT_STARTED';
  const {
    allProgress,
    allProgressLoading,
    allProgressError
  } = useAllProgress({
    language
  }, {
    skip
  });
  if (!allProgressLoading) {
    const progress = allProgress.assetProgress.find(asset => asset.type === type && asset.id === id && (!language || asset.language === language));
    if (progress) {
      status = progress.status;
    }
  }
  return {
    status,
    allProgressLoading,
    allProgressError
  };
};
export const useTrackRedirectUrl = (trackId, language) => {
  const {
    trackProgressById,
    trackProgressByIdLoading,
    trackProgressByIdError
  } = useTrackProgressById(Number(trackId));
  const {
    track,
    trackLoading,
    trackError
  } = useFullTrack(trackId, language);
  const trackRedirectUrl = useMemo(() => {
    if (!trackProgressById || !track) return null;
    const allContentProgress = [];
    if (track.introVideo) {
      allContentProgress.push({
        lessonId: null,
        contentId: trackProgressById.introVideoProgress && trackProgressById.introVideoProgress.contentId,
        status: trackProgressById.introVideoProgress && trackProgressById.introVideoProgress.progressType,
        updatedAt: trackProgressById.introVideoProgress && trackProgressById.introVideoProgress.updatedAt,
        url: `/tracks/${trackId}/intro`
      });
    }
    track.lessons.forEach(lesson => {
      var _trackProgressById$pr;
      const lessonProgress = (_trackProgressById$pr = trackProgressById.progress) === null || _trackProgressById$pr === void 0 ? void 0 : _trackProgressById$pr.find(l => l.lessonId === lesson.id);
      if (lessonProgress) {
        lesson.content.forEach(content => {
          const contentProgress = lessonProgress.contentProgress.find(c => c.contentId === content.id);
          allContentProgress.push({
            lessonId: lesson.id,
            contentId: content.id,
            status: contentProgress && lessonProgress.status !== 'NOT_STARTED' ? contentProgress.progressType : 'NOT_STARTED',
            updatedAt: contentProgress ? contentProgress.updatedAt : null,
            url: `/tracks/${trackId}/${lesson.id}/${content.id}`
          });
        });
      }
    });
    allContentProgress.push({
      lessonId: null,
      contentId: null,
      status: null,
      updatedAt: null,
      url: `/tracks/${trackId}/${track.exam ? 'exam' : 'complete'}`
    });
    if (trackProgressById.status === 'COMPLETED') {
      return allContentProgress[allContentProgress.length - 1].url;
    }
    const lastCompleted = maxBy(allContentProgress.filter(p => p.status === 'COMPLETED'), p => p.updatedAt);
    if (lastCompleted) {
      const lastCompletedIndex = findIndex(allContentProgress, lastCompleted);
      const unprogressedItemsAfterLastCompletedItem = allContentProgress.slice(lastCompletedIndex + 1).filter(p => p.status !== 'COMPLETED');
      return unprogressedItemsAfterLastCompletedItem[0].url;
    }
    return allContentProgress[0].url;
  }, [track, trackId, trackProgressById]);
  return {
    trackRedirectUrl,
    trackRedirectUrlLoading: trackProgressByIdLoading || trackLoading,
    trackRedirectUrlError: trackProgressByIdError || trackError
  };
};
export const useTrackProgress = (trackId, options = {}) => {
  const {
    data: userProgressData,
    loading,
    error
  } = useGetUserProgressQuery({
    variables: {
      requestedProgress: [{
        id: trackId,
        type: 'TRACK'
      }]
    },
    fetchPolicy: options.fetchPolicy,
    pollInterval: options.polling ? 1000 : 0,
    skip: options.skip,
    context: {
      timeout: options.timeout
    }
  });
  const trackProgress = userProgressData === null || userProgressData === void 0 ? void 0 : userProgressData.progress.find(progress => progress.type === 'TRACK');
  return {
    trackProgress,
    trackProgressLoading: loading,
    trackProgressError: unwrapHubHttpError(error)
  };
};